// @ts-nocheck
/**
 * Post Scores (Tourney Machine style). Every game on the working schedule in one
 * table; scores save on blur via rle-score-game, notes via set_game_note.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { invokeEdgeFunction } from "@/lib/invokeEdgeFunction";
import { formatCalendarDate as formatDate, formatTime } from "@/lib/dates";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useIsMobile } from "@/hooks/use-mobile";
import { Loader2 } from "lucide-react";

const MANUAL = "rle-operator-schedule-manual";
const SCORE = "rle-score-game";

type RowState = { a: string; b: string; note: string; status?: "saved" | "error"; msg?: string };

const errMsg = (data: any, error: any) =>
  data?.error?.message || data?.message || error?.message || (data?.ok === false ? "That didn't save." : null);

function gameTitle(g: any) {
  if (g.game_type === "pool") {
    const parts = [];
    if (g.round_number != null) parts.push(`Round ${g.round_number}`);
    parts.push(`Pool Game ${g.game_number ?? ""}`.trim());
    return parts.join(" · ");
  }
  return g.bracket_label || (g.game_number != null ? `Game ${g.game_number}` : "Game");
}

export default function OperatorPostScoresPage() {
  const { eventId } = useParams();
  const isMobile = useIsMobile();
  const [data, setData] = useState<any>(null);
  const [eventName, setEventName] = useState("");
  const [loadErr, setLoadErr] = useState<string | null>(null);
  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [mode, setMode] = useState<"division" | "court">("division");
  const [division, setDivision] = useState<string | null>(null);
  const [court, setCourt] = useState<string | null>(null);
  const [day, setDay] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<"time" | "type">("time");
  const [show, setShow] = useState<"all" | "final" | "notfinal">("all");
  const inputs = useRef<Record<string, HTMLInputElement | null>>({});

  const load = async () => {
    const { data: res, error } = await invokeEdgeFunction(MANUAL, { body: { action: "post_scores_data", event_id: eventId } });
    const m = errMsg(res, error);
    if (m) { setLoadErr(m); return; }
    const d = res?.data ?? res;
    setData(d);
    const next: Record<string, RowState> = {};
    for (const g of d?.games ?? []) next[g.id] = { a: g.score_a ?? "", b: g.score_b ?? "", note: g.public_note ?? "" };
    setRows((prev) => {
      const out = { ...next };
      for (const k of Object.keys(out)) if (prev[k]?.status) out[k] = { ...out[k], status: prev[k].status, msg: prev[k].msg };
      return out;
    });
  };

  useEffect(() => {
    if (!eventId) return;
    load();
    (supabase as any).from("events").select("name").eq("id", eventId).maybeSingle().then(({ data }) => setEventName(data?.name ?? ""));
  }, [eventId]);

  const games: any[] = data?.games ?? [];
  const days = useMemo(() => [...new Set(games.map((g) => g.scheduled_date).filter(Boolean))].sort(), [games]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = games.filter((g) => {
      if (mode === "division" && division && g.division !== division) return false;
      if (mode === "court" && court && g.court_id !== court) return false;
      if (day && g.scheduled_date !== day) return false;
      if (show === "final" && !g.is_final) return false;
      if (show === "notfinal" && g.is_final) return false;
      if (q && !`${g.team_a_name} ${g.team_b_name}`.toLowerCase().includes(q)) return false;
      return true;
    });
    const timeKey = (g: any) => `${g.scheduled_date ?? "9999"} ${g.scheduled_start_time ?? "99"}`;
    const typeOrder: any = { pool: 0, crossover: 1, bracket: 2, consolation: 3 };
    list = [...list].sort((x, y) => {
      if (mode === "court") {
        const cx = (data?.courts ?? []).findIndex((c) => c.id === x.court_id);
        const cy = (data?.courts ?? []).findIndex((c) => c.id === y.court_id);
        if (cx !== cy) return (cx < 0 ? 999 : cx) - (cy < 0 ? 999 : cy);
      }
      if (sort === "type") {
        const t = (typeOrder[x.game_type] ?? 9) - (typeOrder[y.game_type] ?? 9);
        if (t) return t;
        const d = String(x.division ?? "").localeCompare(String(y.division ?? ""));
        if (d) return d;
        return (x.game_number ?? 9999) - (y.game_number ?? 9999);
      }
      const t = timeKey(x).localeCompare(timeKey(y));
      return t || (x.game_number ?? 9999) - (y.game_number ?? 9999);
    });
    return list;
  }, [games, mode, division, court, day, show, search, sort, data]);

  const setRow = (id: string, patch: Partial<RowState>) => setRows((r) => ({ ...r, [id]: { ...r[id], ...patch } }));
  const markSaved = (id: string) => {
    setRow(id, { status: "saved", msg: undefined });
    setTimeout(() => setRows((r) => (r[id]?.status === "saved" ? { ...r, [id]: { ...r[id], status: undefined } } : r)), 1800);
  };
  const markErr = (id: string, msg: string) => setRow(id, { status: "error", msg });

  const callScore = async (g: any, action: string, extra: any = {}) => {
    const { data: res, error } = await invokeEdgeFunction(SCORE, { body: { action, game_id: g.id, ...extra } });
    const m = errMsg(res, error);
    if (m) { markErr(g.id, m); return false; }
    markSaved(g.id);
    await load();
    return true;
  };

  const parse = (v: any) => (v === "" || v == null ? null : Number(v));
  const isBracket = (g: any) => ["bracket", "consolation", "crossover"].includes(g.game_type);

  const onScoreBlur = async (g: any) => {
    const r = rows[g.id];
    const a = parse(r?.a), b = parse(r?.b);
    if (a == null || b == null || Number.isNaN(a) || Number.isNaN(b)) return;
    if (a === g.score_a && b === g.score_b) return;
    if (g.is_final) {
      if (isBracket(g) && a === b) { markErr(g.id, "A bracket game needs a winner."); return; }
      await callScore(g, "complete", { score_a: a, score_b: b });
    } else {
      await callScore(g, "update_score", { score_a: a, score_b: b });
    }
  };

  const onFinal = async (g: any, checked: boolean) => {
    if (!checked) { await callScore(g, "reopen"); return; }
    const r = rows[g.id];
    const a = parse(r?.a), b = parse(r?.b);
    if (a == null || b == null || Number.isNaN(a) || Number.isNaN(b)) { markErr(g.id, "Enter both scores first"); return; }
    if (isBracket(g) && a === b) { markErr(g.id, "A bracket game needs a winner."); return; }
    await callScore(g, "complete", { score_a: a, score_b: b });
  };

  const onNoteBlur = async (g: any) => {
    const note = (rows[g.id]?.note ?? "").trim();
    if (note === (g.public_note ?? "")) return;
    const { data: res, error } = await invokeEdgeFunction(MANUAL, {
      body: { action: "set_game_note", event_id: eventId, game_id: g.id, public_note: note || null },
    });
    const m = errMsg(res, error);
    if (m) { markErr(g.id, m); return; }
    markSaved(g.id);
    await load();
  };

  const onKey = (e: React.KeyboardEvent, idx: number, side: "a" | "b") => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    const target = side === "a" ? `${visible[idx].id}:b` : visible[idx + 1] ? `${visible[idx + 1].id}:a` : null;
    if (target) inputs.current[target]?.focus();
    else (e.target as HTMLInputElement).blur();
  };

  const rowTone = (g: any) => {
    const s = rows[g.id]?.status;
    if (s === "error") return "bg-destructive/10";
    if (s === "saved") return "bg-green-500/25 transition-colors";
    if (g.is_final) return "bg-green-500/10";
    return "";
  };

  const info = (g: any) => {
    const when = g.court_id && g.scheduled_start_time
      ? [g.scheduled_date ? formatDate(g.scheduled_date) : null, formatTime(g.scheduled_start_time), g.court_name].filter(Boolean).join(" · ")
      : "Not scheduled";
    return { title: gameTitle(g), sub: [g.division, when].filter(Boolean).join(" · ") };
  };

  const scoreBox = (g: any, idx: number, side: "a" | "b", cls = "") => (
    <Input
      ref={(el) => { inputs.current[`${g.id}:${side}`] = el; }}
      inputMode="numeric"
      type="text"
      aria-label={side === "a" ? `${g.team_a_name} score` : `${g.team_b_name} score`}
      className={`h-9 w-14 text-center text-base ${cls}`}
      value={rows[g.id]?.[side] ?? ""}
      onChange={(e) => setRow(g.id, { [side]: e.target.value.replace(/[^0-9]/g, "") } as any)}
      onBlur={() => onScoreBlur(g)}
      onKeyDown={(e) => onKey(e, idx, side)}
    />
  );
  const noteBox = (g: any) => (
    <Input
      placeholder="Shows next to score"
      className="h-9 text-sm"
      maxLength={200}
      value={rows[g.id]?.note ?? ""}
      onChange={(e) => setRow(g.id, { note: e.target.value })}
      onBlur={() => onNoteBlur(g)}
    />
  );
  const statusLine = (g: any) => {
    const r = rows[g.id];
    if (r?.status === "error") return <div className="text-xs text-destructive">{r.msg}</div>;
    if (r?.status === "saved") return <div className="text-xs text-green-700">Saved</div>;
    return null;
  };

  if (loadErr) return <div className="p-6 text-sm text-destructive">{loadErr}</div>;
  if (!data) return <div className="flex items-center gap-2 p-6 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading games…</div>;

  const divisions: string[] = data.divisions ?? [];
  const courts: any[] = data.courts ?? [];

  return (
    <div className="w-full space-y-4 p-3 md:p-6">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h1 className="text-2xl font-bold">Post Scores</h1>
          {eventName && <p className="text-sm text-muted-foreground">{eventName}</p>}
        </div>
        <span className="text-xs text-muted-foreground">All changes save automatically</span>
      </div>
      {data.has_schedule && data.published === false && (
        <div className="rounded-md border border-yellow-400 bg-yellow-100 px-3 py-2 text-sm text-yellow-900">
          Not published — the public can't see these scores yet.
        </div>
      )}
      {!data.has_schedule ? (
        <p className="text-sm text-muted-foreground">This event has no schedule yet.</p>
      ) : (
        <div className="flex flex-col gap-4 md:flex-row">
          <aside className="w-full shrink-0 space-y-2 md:w-56">
            <Select value={mode} onValueChange={(v: any) => { setMode(v); setDivision(null); setCourt(null); }}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="division">Show by Division</SelectItem>
                <SelectItem value="court">Show by Court</SelectItem>
              </SelectContent>
            </Select>
            <nav className="space-y-1 text-sm">
              {mode === "division" ? (
                <>
                  <button className={`block w-full rounded px-2 py-1 text-left ${!division ? "bg-muted font-medium" : ""}`} onClick={() => setDivision(null)}>All Divisions</button>
                  {divisions.map((d) => (
                    <div key={d} className={`rounded px-2 py-1 ${division === d ? "bg-muted" : ""}`}>
                      <div className="font-medium">{d}</div>
                      <div className="flex gap-3 text-xs">
                        <button className="text-primary hover:underline" onClick={() => setDivision(d)}>Post Scores</button>
                        <Link className="text-primary hover:underline" to={`/admin-dashboard/events/${eventId}/divisions/${encodeURIComponent(d)}/pools`}>Pools</Link>
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <button className={`block w-full rounded px-2 py-1 text-left ${!court ? "bg-muted font-medium" : ""}`} onClick={() => setCourt(null)}>All Courts</button>
                  {courts.map((c) => (
                    <button key={c.id} className={`block w-full rounded px-2 py-1 text-left ${court === c.id ? "bg-muted font-medium" : ""}`} onClick={() => setCourt(c.id)}>{c.name}</button>
                  ))}
                </>
              )}
            </nav>
          </aside>

          <section className="min-w-0 flex-1 space-y-3">
            <div className="flex flex-wrap gap-1">
              {days.map((d) => (
                <Button key={d} size="sm" variant={day === d ? "default" : "outline"} onClick={() => setDay(d)}>{formatDate(d)}</Button>
              ))}
              <Button size="sm" variant={!day ? "default" : "outline"} onClick={() => setDay(null)}>All days</Button>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Input placeholder="Search teams" className="h-9 w-full sm:w-56" value={search} onChange={(e) => setSearch(e.target.value)} />
              <Select value={sort} onValueChange={(v: any) => setSort(v)}>
                <SelectTrigger className="h-9 w-44"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="time">Sort by date/time</SelectItem>
                  <SelectItem value="type">Sort by type</SelectItem>
                </SelectContent>
              </Select>
              <Select value={show} onValueChange={(v: any) => setShow(v)}>
                <SelectTrigger className="h-9 w-48"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Show all games</SelectItem>
                  <SelectItem value="final">Show final games</SelectItem>
                  <SelectItem value="notfinal">Show not final games</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {visible.length === 0 && <p className="text-sm text-muted-foreground">No games match.</p>}

            {isMobile ? (
              <div className="space-y-2">
                {visible.map((g, idx) => {
                  const i = info(g);
                  return (
                    <div key={g.id} className={`space-y-2 rounded-lg border p-3 ${rowTone(g)}`}>
                      <div>
                        <div className="text-sm font-medium">{i.title}</div>
                        <div className="text-xs text-muted-foreground">{i.sub}</div>
                      </div>
                      <div className="flex items-center justify-between gap-2"><span className="break-words text-sm">{g.team_a_name}</span>{scoreBox(g, idx, "a")}</div>
                      <div className="flex items-center justify-between gap-2"><span className="break-words text-sm">{g.team_b_name}</span>{scoreBox(g, idx, "b")}</div>
                      {noteBox(g)}
                      <label className="flex items-center justify-between rounded-md border px-3 py-2 text-sm font-medium">
                        Final
                        <Switch checked={!!g.is_final} onCheckedChange={(v) => onFinal(g, v)} />
                      </label>
                      {statusLine(g)}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="overflow-x-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-2 py-2 text-left">Game</th>
                      <th className="px-2 py-2 text-right">Team 1</th>
                      <th className="px-2 py-2 text-center" colSpan={2}>Score</th>
                      <th className="px-2 py-2 text-left">Team 2</th>
                      <th className="px-2 py-2 text-left">Note</th>
                      <th className="px-2 py-2 text-center">Final</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visible.map((g, idx) => {
                      const i = info(g);
                      return (
                        <tr key={g.id} className={`border-t align-middle ${rowTone(g)}`}>
                          <td className="px-2 py-2">
                            <div className="font-medium">{i.title}</div>
                            <div className="text-xs text-muted-foreground">{i.sub}</div>
                            {statusLine(g)}
                          </td>
                          <td className="break-words px-2 py-2 text-right">{g.team_a_name}</td>
                          <td className="py-2 pl-2 pr-1 text-right">{scoreBox(g, idx, "a", "ml-auto")}</td>
                          <td className="py-2 pl-1 pr-2">{scoreBox(g, idx, "b")}</td>
                          <td className="break-words px-2 py-2">{g.team_b_name}</td>
                          <td className="min-w-[160px] px-2 py-2">{noteBox(g)}</td>
                          <td className="px-2 py-2 text-center">
                            <Checkbox aria-label="Final" checked={!!g.is_final} onCheckedChange={(v) => onFinal(g, !!v)} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        </div>
      )}
    </div>
  );
}
